import { Button, Image, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { PiMapTrifold } from 'react-icons/pi';
import { memo, useEffect, useState } from 'react';
import { remote_mapData_getThumbnailByDocumentId, remote_mapData_uploadThumbnailByDocumentId } from '~/common/typescript/apis/MapDataAPIs';

interface MapDataThumbnailProps {
  documentId: string;
  mapDataId: number;
  size?: 'small' | 'medium' | 'large';
  editable?: boolean;
  onUploadSuccess?: () => void;
}

const MapDataThumbnail = ({
  documentId,
  mapDataId,
  size = 'medium',
  editable = false,
  onUploadSuccess
}: MapDataThumbnailProps) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Size configurations
  const sizeConfig = {
    small: { width: 60, height: 60 },
    medium: { width: 120, height: 120 },
    large: { width: 200, height: 200 },
  };

  const { width, height } = sizeConfig[size];

  useEffect(() => {
    if (documentId) {
      loadThumbnail();
    }
    
    return () => {
      if (thumbnailUrl && thumbnailUrl.startsWith('blob:')) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    };
  }, [documentId]); // Only re-run when documentId changes, not thumbnailUrl

  const loadThumbnail = async () => {
    try {
      setLoading(true);
      const response = await remote_mapData_getThumbnailByDocumentId(documentId);
      
      if (response instanceof Blob) {
        const objectUrl = URL.createObjectURL(response);
        setThumbnailUrl(objectUrl);
      } 
      else if (response && typeof response === 'object' && 'url' in response) {
        setThumbnailUrl(response.url);
      } 
      else {
        setThumbnailUrl(null);
      }
    } catch (error) {
      setThumbnailUrl(null);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file: File) => {
    try {
      setUploading(true);
      await remote_mapData_uploadThumbnailByDocumentId(documentId, file);
      message.success('Thumbnail uploaded successfully');
      
      // Reload thumbnail
      await loadThumbnail();
      
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      message.error('Failed to upload thumbnail');
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    accept: 'image/*',
    showUploadList: false,
    beforeUpload: (file: File) => {
      // Validate file type
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
        return false;
      }

      // Validate file size (max 5MB)
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('Image must be smaller than 5MB!');
        return false;
      }

      handleUpload(file);
      return false; // Prevent default upload behavior
    },
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      gap: 8,
      width: '100%',
      height: '100%'
    }}>
      {loading ? (
        <div style={{ 
          width: width, 
          height: height, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: 8,
          border: '1px dashed #d9d9d9'
        }}>
          Loading...
        </div>
      ) : thumbnailUrl ? (
        <Image
          src={thumbnailUrl}
          alt={`Thumbnail for map data ${mapDataId}`}
          style={{
            width: width,
            height: height,
            objectFit: 'cover' as const,
            borderRadius: 8,
            border: '1px solid #e8e8e8'
          }}
          preview={false}
        />
      ) : (
        <div style={{ 
          width: width, 
          height: height, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: 8,
          border: '1px dashed #d9d9d9',
          color: '#999',
          fontSize: 12,
          textAlign: 'center' as const,
          padding: 8
        }}>
          <PiMapTrifold style={{ fontSize: 24, color: '#94a3b8' }} />
        </div>
      )}

      {editable && (
        <Upload {...uploadProps}>
          <Button 
            icon={<UploadOutlined />} 
            loading={uploading}
            size="small"
          >
            {thumbnailUrl ? 'Thay đổi' : 'Tải lên'}
          </Button>
        </Upload>
      )}
    </div>
  );
};

export default memo(MapDataThumbnail);
